import { create } from 'zustand';
import { InventoryOrder, ResendPayload } from '@/types/inventory';

/** Carrier data shape matching InventoryOrder['available_carriers_detail'][number] */
type AvailableCarrier = InventoryOrder['available_carriers_detail'][number];

/**
 * RS Ph3 — per-order rule-dispatch state (mirror of AddNewOrder's
 * `ruleDispatch`). `assignedCarrierSlug` is set only for auto-dispatched
 * decisions so ResendStep2 can detect overrides (merchant picks something
 * other than the rule's carrier) and demand a reason.
 */
export interface ResendRuleDispatch {
  action: 'auto-dispatched' | 'auto-cheapest' | 'restricted' | 'manual';
  winningRuleUid: string | null;
  /**
   * RS Ph3 bugfix — preserve the rule's chosen carrier UID so the resend
   * UI can resolve it against the freshly-fetched carrier list (matching
   * AddNewOrder/SelectCarrier semantics) and detect overrides reliably
   * even when slug resolution lags behind.
   */
  assignedCarrierUid?: string | null;
  assignedCarrierSlug: string | null;
  restrictedCarrierUids?: string[];
}

interface ResendState {
  selectedOrders: InventoryOrder[];
  currentStep: number;
  orderOverrides: Record<string, ResendPayload>;
  carrierSelections: Record<string, string>;
  /** Fresh carrier lists fetched after region/data changes in Step 2, keyed by order uid */
  freshCarriers: Record<string, AvailableCarrier[]>;
  /**
   * RS Ph3 — per-order rule-engine decision. null/missing means rules
   * weren't evaluated yet or didn't match (manual flow).
   */
  ruleDispatchByOrder: Record<string, ResendRuleDispatch | null>;

  setSelectedOrders: (orders: InventoryOrder[]) => void;
  setCurrentStep: (step: number) => void;
  setOrderOverride: (uid: string, data: ResendPayload) => void;
  setCarrierForOrder: (uid: string, carrierSlug: string) => void;
  setFreshCarriers: (uid: string, carriers: AvailableCarrier[]) => void;
  setRuleDispatchForOrder: (uid: string, dispatch: ResendRuleDispatch | null) => void;
  reset: () => void;
}

export const useResendStore = create<ResendState>((set) => ({
  selectedOrders: [],
  currentStep: 0,
  orderOverrides: {},
  carrierSelections: {},
  freshCarriers: {},
  ruleDispatchByOrder: {},

  setSelectedOrders: (orders) =>
    set({
      selectedOrders: orders,
      currentStep: 0,
      orderOverrides: {},
      carrierSelections: {},
      freshCarriers: {},
      ruleDispatchByOrder: {},
    }),
  setCurrentStep: (step) => set({ currentStep: step }),
  setOrderOverride: (uid, data) =>
    set((state) => ({
      orderOverrides: { ...state.orderOverrides, [uid]: data },
    })),
  setCarrierForOrder: (uid, carrierSlug) =>
    set((state) => ({
      carrierSelections: { ...state.carrierSelections, [uid]: carrierSlug },
    })),
  setFreshCarriers: (uid, carriers) =>
    set((state) => ({
      freshCarriers: { ...state.freshCarriers, [uid]: carriers },
    })),
  setRuleDispatchForOrder: (uid, dispatch) =>
    set((state) => ({
      ruleDispatchByOrder: { ...state.ruleDispatchByOrder, [uid]: dispatch },
    })),
  reset: () =>
    set({
      selectedOrders: [],
      currentStep: 0,
      orderOverrides: {},
      carrierSelections: {},
      freshCarriers: {},
      ruleDispatchByOrder: {},
    }),
}));
